import React, { useState, useMemo } from "react";

import { useLocales } from "hooks";
import {
  DatePicker,
  Select,
  Canton,
  Option,
  Region,
  MapKind,
} from "components";

export interface FireHistoryFormProps {
  cantons: ReadonlyArray<Canton>;
  regions: ReadonlyArray<Region>;
  onSubmit: (values: FormValues) => void;
  type: MapKind;
}

interface FormValues {
  valid_from: string;
  valid_until: string;
  canton_ids: Array<number>;
  region_ids: Array<number>;
  levelsOrCategories: Array<number>;
}

export const FireHistoryForm: React.FC<FireHistoryFormProps> = ({
  cantons,
  regions = [],
  onSubmit,
  type,
}) => {
  const { t } = useLocales();

  const [values, setValues] = useState<FormValues>({
    valid_from: "",
    valid_until: "",
    canton_ids: [],
    region_ids: [],
    levelsOrCategories: [],
  });

  const handleDatePickerChange =
    (key: keyof FormValues) => (dateStr: string) => {
      const date = window.moment(dateStr, "DD.MM.YYYY");
      const value = date.isValid() ? date.format("YYYY-MM-DD") : "";
      setValues((values) => ({ ...values, [key]: value }));
    };

  const handleSelectChange =
    (key: keyof FormValues) => (ids: Array<number>) => {
      setValues((values) => ({ ...values, [key]: ids }));
    };

  const cantonOptions = useMemo(
    () =>
      cantons.map((canton) => ({
        id: canton.id,
        label: canton.name,
      })),
    [cantons]
  );

  const regionOptions = useMemo(() => {
    const option = (region: Region) => ({ id: region.id, label: region.name });
    if (!values.canton_ids.length) {
      return regions.map(option);
    }

    return regions
      .filter((region) => values.canton_ids.includes(region.canton_id))
      .map(option);
  }, [regions, values.canton_ids]);

  const levelOptions: Array<Option> = [1, 2, 3, 4, 5]
    .map((level) => (type === "warnMap" ? level : level - 1))
    .map((level) => ({
      id: level,
      label: `${level}: ` + t(`legend.${type}.${level}`),
    }));

  return (
    <div className="fire-history-form-component">
      <DatePicker
        label={t("history.form.validFrom")}
        onChange={handleDatePickerChange("valid_from")}
      />
      <DatePicker
        label={t("history.form.validUntil")}
        onChange={handleDatePickerChange("valid_until")}
      />
      <Select
        label={t("history.form.canton")}
        options={cantonOptions}
        multiple
        onChange={(ids) => {
          handleSelectChange("canton_ids")(ids);
          handleSelectChange("region_ids")([]);
        }}
      />
      <Select
        label={t("history.form.region")}
        options={regionOptions}
        multiple
        onChange={handleSelectChange("region_ids")}
        key={values.canton_ids.join(",")}
      />
      <Select
        label={t(`history.form.${type === "warnMap" ? "level" : "category"}`)}
        options={levelOptions}
        onChange={handleSelectChange("levelsOrCategories")}
      />

      <button type="submit" onClick={() => onSubmit(values)}>
        {t("history.form.submit")}
      </button>
    </div>
  );
};
